import { cn } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import {
  BoardBoxElement,
  BoardRotatedGroup,
  ShelfSurface,
} from "@/shelf/components/shelf-board/board-surface";
import { ShelfSpineItem } from "@/shelf/components/shelf-board/shelf-spine";
import {
  isShelfFinishNearBlack,
  normalizeShelfColor,
  resolveShelfInkTone,
} from "@/shelf/utils/shelf-appearance";
import { resolveSlotBox } from "@/shelf/utils/shelf-layout";

import type { BoardBox, ShelfLayout, ShelfSpine } from "@/shelf/types/types";

/**
 * One rack: a coloured carcass with its name standing on top of it, the bays
 * inside it, and the discs standing in some of them.
 *
 * Drawn as the desktop draws it, part for part: flat and square, one fill and
 * one hairline edge for the body and its name plate together, a recess the
 * bays sit in, and the cases standing in their bays. A rack looked at here has
 * to be recognisably the rack that was built there.
 *
 * **Nothing here is a control except the discs.** This application reads Shelves
 * and writes none of them, so a rack cannot be selected, moved, turned, renamed
 * or filled from the browser — and a control that can never apply is absent
 * rather than disabled. An empty bay is a place a disc could stand, drawn so
 * the rack shows where its slots are; it is not a button, because pressing it
 * here could do nothing.
 *
 * Everything is placed in millimeters, because the surface above it is scaled so
 * one unit is one millimeter — so the hairlines here are millimeters too, and
 * grow and shrink with the rack they belong to.
 */
export const ShelfRack = ({
  layout,
  spinesBySlotIndex,
}: {
  layout: ShelfLayout;
  spinesBySlotIndex: ReadonlyMap<number, ShelfSpine>;
}) => {
  const { t } = useTranslation();
  const { boundingBox, interior, namePlate, outer, rotationDegrees, shelf } = layout;
  const color = normalizeShelfColor(shelf.color);
  const relative = (box: BoardBox): BoardBox => ({
    ...box,
    xMillimeters: box.xMillimeters - outer.xMillimeters,
    yMillimeters: box.yMillimeters - outer.yMillimeters,
  });

  return (
    <ShelfSurface
      color={color}
      inkTone={resolveShelfInkTone(color)}
      isNearBlack={isShelfFinishNearBlack(color)}
    >
      {/* The rack's whole surface — name plate and body as one upright box,
          one fill, one edge, one shadow — so the name plate reads as part of
          the rack rather than as a second object laid beside it. */}
      <BoardBoxElement
        aria-hidden
        box={boundingBox}
        className="bg-(--shelf-body) shadow-[0_1px_2px_rgba(0,0,0,0.22)] ring-[0.35px] ring-(--shelf-edge)"
      />

      <BoardRotatedGroup
        box={outer}
        rotationDegrees={rotationDegrees}
        pivotXMillimeters={outer.widthMillimeters / 2}
        pivotYMillimeters={outer.heightMillimeters / 2}
      >
        <BoardBoxElement
          aria-hidden
          box={relative(interior)}
          className="bg-(--shelf-bay) shadow-[inset_0_1px_2px_rgba(0,0,0,0.35)]"
        />

        {Array.from({ length: shelf.capacity }, (_unused, slotIndex) => {
          const spine = spinesBySlotIndex.get(slotIndex);
          const box = relative(resolveSlotBox({ layout, slotIndex }));
          return spine ? (
            <ShelfSpineItem key={slotIndex} box={box} spine={spine} />
          ) : (
            <EmptyBay key={slotIndex} box={box} isFirstSlot={slotIndex === 0} />
          );
        })}
      </BoardRotatedGroup>

      {/* The name, standing on top of the rack as it stands, so it reads
          upright at every angle: a label lying on its side is a label nobody
          reads. Held to the rack's own width and truncated, so a long name
          stops at the rack rather than running across its neighbour. */}
      <BoardBoxElement
        box={namePlate}
        role="presentation"
        className="flex items-center gap-[4px] overflow-hidden border-b-[0.35px] border-(--shelf-edge) px-[6px] text-(--shelf-ink)"
      >
        <span className="min-w-0 flex-1 truncate text-left text-[6px] font-semibold leading-none">
          {shelf.name}
        </span>
        <span aria-hidden className="shrink-0 text-[4.5px] font-medium leading-none opacity-75">
          {t("shelf.rack.occupancy", {
            capacity: shelf.capacity,
            count: spinesBySlotIndex.size,
          })}
        </span>
      </BoardBoxElement>
    </ShelfSurface>
  );
};

/**
 * A slot with nothing in it, drawn as a **bay**: a compartment with the rack
 * showing through the hairline between it and the next, so a rack shows where
 * its slots are without outlining every one.
 *
 * Decoration here, unlike on the desktop where a bay is the control that fills
 * it. Nothing in this application can put a disc on a rack, so the bay carries
 * no name, no focus and no press.
 */
const EmptyBay = ({ box, isFirstSlot }: { box: BoardBox; isFirstSlot: boolean }) => (
  <BoardBoxElement
    aria-hidden
    box={box}
    // The hairline is a shadow, not a border, as on the desktop: a border takes
    // room inside the bay.
    className={cn(
      "bg-(--shelf-bay)",
      !isFirstSlot && "shadow-[inset_0.35px_0_0_var(--shelf-divider)]",
    )}
  />
);
